import { IonButton, IonSpinner } from '@ionic/react';
import { ComponentProps, FC, useMemo } from 'react';
import classnames from 'classnames';
import useTheme from '../../../../hooks/ui/useTheme';

type ButtonDesign = 'primary' | 'secondary' | 'tertiary' | 'outline' | 'link';

interface Props extends ComponentProps<typeof IonButton> {
  title?: string;
  icon?: JSX.Element;
  loading?: boolean;
  className?: string;
  design?: ButtonDesign;
  children?: JSX.Element;
}

const Button: FC<Props> = ({ title, icon, className, loading, design, children, ...rest }): JSX.Element => {
  const theme = useTheme();
  const spinnerColor = useMemo(() => {
    switch (design) {
      case 'secondary':
        return theme.color.black.DEFAULT;
      case 'tertiary':
        return theme.color.mobile.tertiary;
      default:
        return theme.color.white.DEFAULT;
    }
  }, [design, theme]);

  const Content = (): JSX.Element => {
    if (loading) {
      return (
        <span className="flex items-center justify-center">
          <IonSpinner name="crescent" color={spinnerColor} />
        </span>
      );
    }

    if (children) {
      return children;
    }

    return (
      <>
        {icon && (
          <span slot="start" className="mr-2">
            {icon}
          </span>
        )}
        <span>{title}</span>
      </>
    );
  };
  switch (design) {
    case 'link':
      return (
        <IonButton
          {...rest}
          className={classnames(
            'text-brand-primary font-semibold text-base min-h-5 [&::part(native)]:w-fit [&::part(native)]:p-none [&::part(native)]:m-0 [&::part(native)]:bg-transparent',
            className,
          )}
          fill="clear"
        >
          <Content />
        </IonButton>
      );
    case 'secondary':
      return (
        <IonButton
          {...rest}
          className={classnames(
            'text-brand-text-primary font-semibold [&::part(native)]:min-h-[45px] [&::part(native)]:max-h-[45px] [&::part(native)]:rounded-lg [&::part(native)]:bg-mobile-notice m-0',
            className,
            {
              '[&::part(native)]:!bg-gray-900 !text-brand-text-secondary': rest?.disabled,
            },
          )}
          fill="clear"
        >
          <Content />
        </IonButton>
      );
    case 'tertiary':
      return (
        <IonButton
          {...rest}
          className={classnames(
            'rounded-lg [&::part(native)]:min-h-[43px] [&::part(native)]:max-h-[43px] bg-white w-full !text-brand-text-secondary font-semibold bordered m-0',
            className,
            {
              '[&::part(native)]:!bg-gray-900 !text-brand-text-secondary': rest?.disabled,
            },
          )}
          fill="clear"
        >
          <Content />
        </IonButton>
      );
    case 'outline':
      return (
        <IonButton {...rest} className={classnames('m-0', className)} fill="clear">
          <Content />
        </IonButton>
      );
    default:
      return (
        <IonButton
          {...rest}
          fill="clear"
          className={classnames(
            'text-white font-semibold [&::part(native)]:min-h-[45px] [&::part(native)]:max-h-[45px] [&::part(native)]:rounded-lg [&::part(native)]:bg-brand-primary [&::part(native)]:w-full w-full m-0',
            className,
            {
              '[&::part(native)]:!bg-gray-900 !text-brand-text-secondary': rest?.disabled,
            },
          )}
        >
          <Content />
        </IonButton>
      );
  }
};

export default Button;
